/**
 * Renewables Step - Form Component
 *
 * Step 4 of the Energy Certificate Simulator wizard.
 * Collects renewable energy information: PV system, other renewables.
 */

import {
  Stack,
  Switch,
  NumberInput,
  Checkbox,
  Text,
  Paper,
  Group,
  ThemeIcon,
  Badge,
  Collapse,
  Alert,
} from '@mantine/core';
import {
  IconSolarPanel,
  IconSun,
  IconWind,
  IconLeaf,
  IconInfoCircle,
} from '@tabler/icons-react';
import type { UseFormReturnType } from '@mantine/form';
import type { SimulatorInput, RenewableSource } from '../../types';

// =============================================================================
// CONSTANTS
// =============================================================================

const RENEWABLE_SOURCES: { value: RenewableSource; label: string; description: string; epReduction: string }[] = [
  {
    value: 'solar_thermal',
    label: 'Kolektory słoneczne',
    description: 'System podgrzewania CWU za pomocą energii słonecznej',
    epReduction: '-10 do -25 kWh/m²/rok',
  },
  {
    value: 'recuperation',
    label: 'Rekuperacja (wentylacja z odzyskiem ciepła)',
    description: 'Mechaniczna wentylacja z odzyskiem ciepła z powietrza wywiewanego',
    epReduction: '-15 do -40 kWh/m²/rok',
  },
  {
    value: 'ground_exchanger',
    label: 'Gruntowy wymiennik ciepła (GWC)',
    description: 'Podgrzewanie/chłodzenie powietrza wentylacyjnego w gruncie',
    epReduction: '-5 do -15 kWh/m²/rok',
  },
];

// =============================================================================
// COMPONENT
// =============================================================================

export interface RenewablesStepProps {
  /** Mantine form instance */
  form: UseFormReturnType<SimulatorInput>;
}

/**
 * Renewable energy data collection step
 *
 * Collects:
 * - PV system presence and power
 * - Other renewable energy sources (solar thermal, recuperation, GWC)
 */
export function RenewablesStep({ form }: RenewablesStepProps) {
  const hasPV = form.values.hasPV;
  const selectedRenewables = form.values.renewables || [];

  const handleRenewableToggle = (source: RenewableSource, checked: boolean) => {
    if (checked) {
      form.setFieldValue('renewables', [...selectedRenewables, source]);
    } else {
      form.setFieldValue(
        'renewables',
        selectedRenewables.filter((s) => s !== source)
      );
    }
  };

  return (
    <Stack gap="lg">
      {/* Photovoltaic system */}
      <Paper p="md" withBorder>
        <Stack gap="md">
          <Group gap="xs">
            <ThemeIcon variant="light" color="yellow" size="lg">
              <IconSolarPanel size={20} />
            </ThemeIcon>
            <div>
              <Text fw={500} size="sm">
                Instalacja fotowoltaiczna (PV)
              </Text>
              <Text size="xs" c="dimmed">
                Panele słoneczne produkujące energię elektryczną
              </Text>
            </div>
          </Group>

          <Switch
            label="Posiadam instalację fotowoltaiczną"
            checked={hasPV}
            onChange={(event) =>
              form.setFieldValue('hasPV', event.currentTarget.checked)
            }
            aria-label="Czy posiadasz instalację fotowoltaiczną"
          />

          <Collapse in={hasPV}>
            <Paper p="sm" bg="gray.0" radius="sm">
              <Stack gap="xs">
                <NumberInput
                  label="Moc instalacji PV"
                  description="Moc znamionowa w kilowatach peak"
                  placeholder="np. 5.0"
                  min={0.5}
                  max={50}
                  step={0.5}
                  decimalScale={1}
                  suffix=" kWp"
                  {...form.getInputProps('pvPower')}
                  aria-label="Moc instalacji PV w kWp"
                />
                <Text size="xs" c="dimmed">
                  Typowe instalacje domowe: 3-10 kWp. Sprawdź na fakturze od instalatora.
                </Text>
              </Stack>
            </Paper>
          </Collapse>

          {/* PV benefits info */}
          <Paper p="sm" bg="yellow.0" radius="sm">
            <Group gap="xs">
              <IconSun size={16} color="var(--mantine-color-yellow-8)" />
              <Text size="xs" c="yellow.8">
                <strong>Korzyść:</strong> Instalacja PV 5 kWp może obniżyć EP o 20-40 kWh/m²/rok
              </Text>
            </Group>
          </Paper>
        </Stack>
      </Paper>

      {/* Other renewable sources */}
      <Paper p="md" withBorder>
        <Stack gap="md">
          <Group gap="xs">
            <ThemeIcon variant="light" color="green" size="lg">
              <IconLeaf size={20} />
            </ThemeIcon>
            <div>
              <Text fw={500} size="sm">
                Inne odnawialne źródła energii
              </Text>
              <Text size="xs" c="dimmed">
                Dodatkowe systemy zmniejszające zużycie energii
              </Text>
            </div>
          </Group>

          <Stack gap="sm">
            {RENEWABLE_SOURCES.map((source) => (
              <Paper key={source.value} p="sm" withBorder radius="sm">
                <Group justify="space-between" wrap="nowrap">
                  <Checkbox
                    label={
                      <div>
                        <Text size="sm">{source.label}</Text>
                        <Text size="xs" c="dimmed">
                          {source.description}
                        </Text>
                      </div>
                    }
                    checked={selectedRenewables.includes(source.value)}
                    onChange={(event) =>
                      handleRenewableToggle(source.value, event.currentTarget.checked)
                    }
                    aria-label={source.label}
                  />
                  <Badge size="sm" variant="light" color="green">
                    {source.epReduction}
                  </Badge>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Stack>
      </Paper>

      {/* Summary of selections */}
      {(hasPV || selectedRenewables.length > 0) && (
        <Alert
          variant="light"
          color="green"
          title="Wybrane systemy OZE"
          icon={<IconWind size={18} />}
        >
          <Stack gap="xs">
            {hasPV && form.values.pvPower && (
              <Text size="sm">
                • Fotowoltaika: {form.values.pvPower} kWp
              </Text>
            )}
            {selectedRenewables.map((source) => {
              const sourceInfo = RENEWABLE_SOURCES.find((s) => s.value === source);
              return (
                <Text key={source} size="sm">
                  • {sourceInfo?.label}
                </Text>
              );
            })}
          </Stack>
        </Alert>
      )}

      {/* Info box */}
      <Paper p="md" bg="green.0">
        <Group gap="xs" align="flex-start">
          <IconInfoCircle size={18} color="var(--mantine-color-green-8)" />
          <Text size="xs" c="green.8">
            <strong>Wskazówka:</strong> Odnawialne źródła energii znacząco obniżają
            współczynnik EP i mogą zmienić klasę energetyczną budynku. Rekuperacja
            w połączeniu z pompą ciepła daje najlepsze efekty.
          </Text>
        </Group>
      </Paper>
    </Stack>
  );
}

export default RenewablesStep;
